import { createResource, For, JSX, Match, Show, Switch } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";
import {
  CoinsTransactionData,
  CoinsTransactionsResponse,
} from "~/data/transaction";
import { clientRepo } from "~/server/apis/client_repo";
import { toIndianNumber } from "~/utils/number";
import { formatDateShort } from "~/utils/date";
import { RedeemCodeModalTelescope } from "./redeem_code_modal_telescope";
import { openModalV2 } from "~/shared_states/modal";
import { coinIcon } from "~/assets/assets";
import { BorderGradientTelescope } from "../border_gradient/border_gradient_telescope";
import { openCoinsCreditedMessageTelescope } from "./welcome_message_telescope";

// Asset URLs from Figma
const arrowUpRight = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M7.05005 16.9498L16.95 7.0498"
        stroke="#178C4E"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M7.05005 9.9502V16.9502"
        stroke="#178C4E"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M14.05 16.9502H7.05005"
        stroke="#178C4E"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};
const arrowDownLeft = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M16.95 7.0502L7.04995 16.9502"
        stroke="#5C5C5C"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M16.95 14.0498V7.0498"
        stroke="#5C5C5C"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M9.94995 7.0498H16.95"
        stroke="#5C5C5C"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

export type CoinsTransactionsModalTelescopeProps = {
  balance: number;
  userName: string;
  enableRedeemCode: boolean;
  headers?: Record<string, string>;
  onClose: () => void;
  onError?: (error: any) => void;
  refreshV4HomePage: () => Promise<void>;
  singleBrandName?: string;
};

export function CoinsTransactionsModalTelescope(
  props: CoinsTransactionsModalTelescopeProps
) {
  const { components } = useTelescopeContext();
  const [response] = createResource<CoinsTransactionsResponse>(async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const response = await clientRepo.getCoinTransactions(props.headers);
      return response;
    } catch (error) {
      props.onError?.(error);
      throw error;
    }
  });

  const openRedeemCodeModal = () => {
    openModalV2(
      () => (
        <RedeemCodeModalTelescope
          userName={props.userName}
          refreshV4HomePage={props.refreshV4HomePage}
          onSuccess={(amount) => {
            openCoinsCreditedMessageTelescope({
              coinsCredited: amount,
              userName: props.userName,
              totalBalance: props.balance,
              singleBrandName: props.singleBrandName,
            });
            // Success callback - can show success message if needed
          }}
        />
      ),
      {
        size: "small",
      }
    );
  };

  return (
    <div class="flex h-full w-full flex-col  bg-background-normal-primary">
      {/* Modal Header */}
      <components.ModalHeader
        size="large"
        title="Your coins"
        description="Use them to purchase gift card(s)"
        icon={() => (
          <img src={coinIcon} alt="coin" class="size-full object-cover" />
        )}
        onClose={props.onClose}
      />

      {/* Content */}
      <div class="flex w-full flex-col gap-4 px-5 pb-5">
        {/* Balance Card */}
        <BorderGradientTelescope>
          <div class="flex w-full flex-col items-center gap-3 rounded-[12px]  border-2 border-stroke-1  bg-background-normal-primary p-4">
            <div class="flex w-full flex-col items-center gap-0.5 text-center">
              <div class="text-label-regular text-text-normal-secondary">
                Balance
              </div>
              <div class="text-[32px] font-semibold leading-[40px] tracking-[-0.16px] text-text-normal-primary">
                ₹{toIndianNumber(props.balance)}
              </div>
            </div>

            <Show when={props.enableRedeemCode}>
              <components.Button
                text="Redeem code"
                onClick={openRedeemCodeModal}
                variant="primary"
                style="neutral"
                size={36}
                expanded
              />
            </Show>
          </div>
        </BorderGradientTelescope>

        {/* Transaction History */}
        <div class="flex w-full flex-col overflow-hidden rounded-xl border border-stroke-1">
          {/* Header */}
          <div class="flex h-8 items-center px-3">
            <div class="text-[10px] font-medium uppercase leading-[12px] tracking-[0.4px] text-text-normal-secondary">
              Transaction history
            </div>
          </div>

          {/* Transaction List */}
          <div class="flex  flex-col ">
            <Switch>
              <Match when={response.state === "ready"}>
                <Show
                  when={response()!.data.length > 0}
                  fallback={
                    <div class="flex flex-col items-center justify-center py-8 text-center">
                      <p class="text-para-3-medium text-text-normal-primary">
                        No transactions yet
                      </p>
                      <p class="text-label-regular text-text-normal-secondary">
                        Your coin transactions will appear here.
                      </p>
                    </div>
                  }
                >
                  <For each={response()!.data}>
                    {(transaction) => (
                      <TransactionItem transaction={transaction} />
                    )}
                  </For>
                </Show>
              </Match>

              <Match when={response.state === "pending"}>
                <div class="flex  items-center justify-center py-8">
                  <components.CircularLoader size={20} variant="primary" />
                </div>
              </Match>

              <Match when={response.state === "errored"}>
                <div class="px-3 py-4 text-para-3-regular text-error-base">
                  {response.error?.message || "Failed to load transactions"}
                </div>
              </Match>
            </Switch>
          </div>
        </div>
      </div>
    </div>
  );
}

function TransactionItem(props: { transaction: CoinsTransactionData }) {
  const isCredit = props.transaction.transactionType === "CREDIT";

  return (
    <div class="flex  items-center gap-3 bg-background-normal-primary px-3 py-2.5">
      {/* Icon */}
      <div
        class="flex size-10 shrink-0 items-center justify-center rounded-[20px]"
        classList={{
          "bg-[rgba(31,193,107,0.1)]": isCredit,
          "bg-[rgba(159,159,169,0.1)]": !isCredit,
        }}
      >
        {isCredit ? arrowUpRight() : arrowDownLeft()}
      </div>

      {/* Text Content */}
      <div class="flex min-w-0 flex-1 flex-col gap-1">
        <div class="truncate text-para-3-medium text-text-normal-primary">
          {props.transaction.remarks}
        </div>
        <div class="truncate text-label-regular text-text-normal-secondary">
          {formatDateShort(new Date(props.transaction.dateTime))}
        </div>
      </div>

      {/* Amount */}
      <div class="flex shrink-0 items-center text-label-medium text-text-normal-secondary">
        {isCredit ? "+" : "-"}
        {toIndianNumber(props.transaction.amount)}
      </div>

      {/* Chevron */}
      {/* {chevronRight()} */}
    </div>
  );
}

const chevronRight = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M8.33337 13.3332L11.6667 9.99984L8.33337 6.6665"
        stroke="#A3A3A3"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};
